import React, { useCallback, useMemo } from 'react';
import { ReactComponent as Cancel } from 'assets/icons/iconClose.svg';
import { ReactComponent as CalendarIcon } from 'assets/icons/calendar.svg';
import { ReactComponent as GoogleCalendarIcon } from 'assets/icons/google-calendar.svg';
import { ReactComponent as OutlookIcon } from 'assets/icons/outlook-icon.svg';

import { UpcomingAppoinment } from '../../interfaces';
import { createSearchParams, useNavigate } from 'react-router-dom';
import {
  MY_APPOINTMENTS_DETAILS,
  MY_APPOINTMENTS_RESCHEDULE_AN_APPOINTMENT,
  MY_PATIENTS,
  MY_PATIENTS_BIO_INFO,
  MY_PATIENTS_BIO_INFO_PATIENT_INFO,
  PROVIDERS_PROVIDER_BIO,
} from 'utilities/routes';
import { useMyAppointmentsLocale } from '../../pages/MyAppointments';
import { CANCEL_APPOINTMENT_ID } from './helper/const';
import moment from 'moment';
import {
  AppointmentHasuraStatus,
  MIN_RESCHEDULE_TIME,
} from '../../const/appointment';
import UpcomingAppointmentCardMobile from './UpcomingAppointmentCardMobile';
import UpcomingAppointmentsCardDesktop from './UpcomingAppointmentsCardDesktop';
import UpcomingDnaAppointmentCardDesktop from './UpcomingDnaAppointmentCardDesktop';
import { useAuth } from 'auth/context/AuthContext';
import { Roles } from '../../../../firebase/interfaces';
import { useAddToCalendar } from 'hooks/useAddToCalendar';
import { mapUpcomingAppointmentToCalendarParams } from 'utilities/mapAppointmentToCalendarParams';
import CustomTooltip from 'components/CustomTooltip';
import { AppointmentTypeEnum } from 'graphql/generated/hasura';
import UpcomingAppointmentDnaCardMobile from './UpcomingAppointmentDnaCardMobile';

interface IUpcomingAppointmentCardProps {
  appointment: UpcomingAppoinment;
  tooltipInfo?: boolean;
}

const UpcomingAppointmentCard: React.FC<IUpcomingAppointmentCardProps> = ({
  appointment,
  tooltipInfo = false,
}) => {
  const navigate = useNavigate();

  const {
    providerId,
    name,
    status,
    appointmentStatus,
    date,
    appointmentType,
    end,
    subjectOfAppointment,
    appointmentId,
    patientId,
    paymentInformation,
    lastProcessedDate,
    appointmentUrl,
    paymentSessionOpenedAt,
  } = appointment;

  const { locale } = useMyAppointmentsLocale();
  const { user } = useAuth();
  const tooltipId = 'pastTimeReschedule';

  const isProvider = useMemo(() => user?.role === Roles.PROVIDER, [user]);

  const hasPastTimeRescheduleAppointment = useMemo(() => {
    const duration = moment.duration(moment(date).diff(moment()));
    return duration.asHours() < MIN_RESCHEDULE_TIME;
  }, [date]);

  const hasPastTimeCancelAppointment = useMemo(() => {
    return moment(date).isBefore(moment());
  }, [date]);

  const { addToGoogleCalendar, addToOutlookCalendar } = useAddToCalendar();

  const options = useMemo(
    () => [
      {
        Icon: GoogleCalendarIcon,
        label: locale.addToGoogleCalendar || 'Add to Google calendar',
        onClick: () =>
          addToGoogleCalendar(
            mapUpcomingAppointmentToCalendarParams(appointment, isProvider),
          ),
      },
      {
        Icon: OutlookIcon,
        label: locale.addToOutlookCalendar || 'Add to Outlook calendar',
        onClick: () =>
          addToOutlookCalendar(
            mapUpcomingAppointmentToCalendarParams(appointment, isProvider),
          ),
      },
      { divider: true },
      {
        Icon: CalendarIcon,
        label: isProvider ? 'Request new time' : locale?.reschedule,
        disabled: hasPastTimeRescheduleAppointment,
        tooltipId:
          !isProvider && hasPastTimeRescheduleAppointment
            ? tooltipId
            : undefined,
        onClick: () =>
          navigate(
            `${MY_APPOINTMENTS_RESCHEDULE_AN_APPOINTMENT}/${appointmentId}`,
          ),
      },
      {
        Icon: Cancel,
        label: locale?.cancelAppointment,
        iconClassName: 'stroke-clc-blue stroke-2',
        disabled: hasPastTimeCancelAppointment,
        onClick: () =>
          navigate({
            search: createSearchParams({
              [CANCEL_APPOINTMENT_ID]: appointmentId,
            }).toString(),
          }),
      },
    ],
    [
      addToGoogleCalendar,
      addToOutlookCalendar,
      appointment,
      appointmentId,
      hasPastTimeCancelAppointment,
      hasPastTimeRescheduleAppointment,
      isProvider,
      locale,
      navigate,
    ],
  );

  const handleProviderClick = useCallback(() => {
    return navigate(PROVIDERS_PROVIDER_BIO, {
      state: { providerId: providerId },
    });
  }, [providerId, navigate]);

  const handlePatientClick = useCallback(() => {
    return navigate(
      `${MY_PATIENTS}/${appointment.patientId}/${MY_PATIENTS_BIO_INFO}/${MY_PATIENTS_BIO_INFO_PATIENT_INFO}`,
    );
  }, [appointment.patientId, navigate]);

  const handleNameClick = isProvider ? handlePatientClick : handleProviderClick;

  const handleDetailsClick = useCallback(() => {
    return navigate(`${MY_APPOINTMENTS_DETAILS}/${appointmentId}`, {
      state: { appointmentId },
    });
  }, [appointmentId, navigate]);

  return (
    <>
      <CustomTooltip
        content="Rescheduling isn't available 24 hours before the appointment start time"
        place="left"
        className="max-w-[240px]"
        id={tooltipId}
      />
      {appointmentType === AppointmentTypeEnum.Dnavisit && (
        <>
          <UpcomingDnaAppointmentCardDesktop
            appointmentStatus={appointmentStatus as AppointmentHasuraStatus}
            appointmentId={appointmentId}
            appointmentType={appointmentType}
            paymentSessionOpenedAt={paymentSessionOpenedAt}
            name={name}
            date={date}
            end={end}
            status={status}
            subjectOfAppointment={subjectOfAppointment}
            patientId={patientId}
            providerId={providerId}
            paymentInformation={paymentInformation}
            lastProcessedDate={lastProcessedDate}
            appointmentUrl={appointmentUrl}
          />
          <UpcomingAppointmentDnaCardMobile
            appointmentStatus={appointmentStatus as AppointmentHasuraStatus}
            appointmentId={appointmentId}
            appointmentType={appointmentType}
            paymentSessionOpenedAt={paymentSessionOpenedAt}
            name={name}
            date={date}
            end={end}
            status={status}
            subjectOfAppointment={subjectOfAppointment}
            options={options}
            handleNameClick={handleNameClick}
            patientId={patientId}
            providerId={providerId}
            paymentInformation={paymentInformation}
            lastProcessedDate={lastProcessedDate}
            handleDetailsClick={handleDetailsClick}
          />
        </>
      )}
      {appointmentType !== AppointmentTypeEnum.Dnavisit && (
        <>
          <UpcomingAppointmentsCardDesktop
            appointmentStatus={appointmentStatus as AppointmentHasuraStatus}
            appointmentId={appointmentId}
            appointmentType={appointmentType}
            name={name}
            date={date}
            end={end}
            status={status}
            subjectOfAppointment={subjectOfAppointment}
            options={options}
            handleNameClick={handleNameClick}
            patientId={patientId}
            providerId={providerId}
            paymentInformation={paymentInformation}
            lastProcessedDate={lastProcessedDate}
            tooltipInfo={tooltipInfo}
            paymentSessionOpenedAt={paymentSessionOpenedAt}
            handleDetailsClick={handleDetailsClick}
          />
          <UpcomingAppointmentCardMobile
            appointmentStatus={appointmentStatus as AppointmentHasuraStatus}
            appointmentId={appointmentId}
            appointmentType={appointmentType}
            name={name}
            date={date}
            end={end}
            status={status}
            subjectOfAppointment={subjectOfAppointment}
            options={options}
            handleNameClick={handleNameClick}
            patientId={patientId}
            paymentSessionOpenedAt={paymentSessionOpenedAt}
            providerId={providerId}
            paymentInformation={paymentInformation}
            lastProcessedDate={lastProcessedDate}
            handleDetailsClick={handleDetailsClick}
          />
        </>
      )}
    </>
  );
};

export default UpcomingAppointmentCard;
